/**
 * Style module.
 * @module src/style
 */

/**
 * Returns the CSS Propert value for an DOM element.
 *
 * @function getCSSProperty
 *
 * @param {HTMLElement} element - the HTML element
 * @param {string} property - the property name we whant to have the computed CSS value
 * @return {string} - the computed CSS value
 *
 * @example
 * const element = document.createElement('div');
 * getCSSProperty(element, 'display');
 * // returns "block"
 *
 * @example
 * const element = document.createElement('div');
 * getCSSProperty(element, 'width');
 * // returns "216px"
 *
 */
export const getCSSProperty = (element, property) => window.getComputedStyle(element).getPropertyValue(property);
/**
 * Add a CSS class from elements.
 *
 * @function addClass
 *
 * @param {(HTMLElement|HTMLElement[])} elements - an HTML element or a list of HTML elements
 * @param {string} klass - the class to be added
 *
 * @example
 * const element = document.createElement('div');
 * addClass(element, 'Active');
 * // adds the class to element
 *
 * @example
 * const element = document.createElement('div');
 * addClass(element, 'Active');
 * // adds the class to element
 * addClass(element, 'Active');
 * // does nothing
 *
 * @example
 * const div = document.createElement('div');
 * const paragraph = document.createElement('p');
 * addClass(paragraph, 'Active');
 * removeClass([div, paragraph], 'Active');
 * // adds the class 'Active' to div element
 *
 */

export const addClass = (elements, klass) => {
  [elements].flat().forEach(element => element.classList.add(...[klass].flat()));
};
/**
 * Removes a CSS class from elements.
 *
 * @function removeClass
 *
 * @param {(HTMLElement|HTMLElement[])} elements - an HTML element or a list of HTML elements
 * @param {string} klass - the class to be removed
 *
 * @example
 * const element = document.createElement('div');
 * removeClass(element, 'Active');
 * // does nothing
 *
 * @example
 * const element = document.createElement('div');
 * addClass(element, 'Active');
 * removeClass(element, 'Active');
 * // removes the class 'Active'
 *
 * @example
 * const div = document.createElement('div');
 * const paragraph = document.createElement('p');
 * addClass(paragraph, 'Active');
 * removeClass([div, paragraph], 'Active');
 * // removes the class 'Active' from paragraph element
 *
 */

export const removeClass = (elements, klass) => {
  [elements].flat().forEach(element => element.classList.remove(...[klass].flat()));
};
/**
 * Returns if an element has or not some CSS class.
 *
 * @function hasClass
 *
 * @param {HTMLElement} element - the HTML element
 * @param {string} klass- the class to be checked
 * @return {boolean} - if the element has or not the CSS class
 *
 * @example
 * const element = document.createElement('div');
 * hasClass(element, 'Active');
 * // returns false
 *
 * @example
 * const element = document.createElement('div');
 * addClass(element, 'div');
 * hasClass(element, 'Active');
 * // returns true
 *
 */

export const hasClass = (element, klass) => element.classList.contains(klass);
/**
 * Add a CSS class if the tag does not have the class, and removes the class if the tag has the class.
 *
 * @function toggleClass
 *
 * @param {(HTMLElement|HTMLElement[])} elements - an HTML element or a list of HTML elements
 * @param {string} klass - the class to be toggled
 *
 * @example
 * const element = document.createElement('div');
 * toggleClass(element, 'Active');
 *
 * @example
 * const div = document.createElement('div');
 * const paragraph = document.createElement('p');
 * toggleClass([div, paragraph], 'Active');
 *
 */

export const toggleClass = (elements, klass) => {
  [elements].flat().forEach(el => el.classList.toggle(klass));
};
/**
 * Remove the class `hidden` on element to show and add it to the element to hide.
 *
 * @function replaceVisibleElement
 *
 * @param {Object} elements - Elements to hide and show
 * @param {(HTMLElement|HTMLElement[])} elements.toHide - the HTML element to hide
 * @param {(HTMLElement|HTMLElement[])} elements.toShow - the HTML element to show
 *
 * @example
 * const elementToHide = document.createElement('div');
 * const elementToShow = document.createElement('div');
 * elementToShow.classList.add('hidden')
 * replaceVisibleElement({toHide: elmentToHide, toShow: elementToShow});
 *
 */

export const replaceVisibleElement = _ref => {
  let toHide = _ref.toHide,
      toShow = _ref.toShow;
  addClass(toHide, 'hidden');
  removeClass(toShow, 'hidden');
};